import { firstBy } from "thenby";

import type { Card, Distributor, FilterTypeEnum } from "@sorcery/db";
import {
  CollectionItemConditionEnum,
  DistributorRegionEnum,
  SetCodeEnum,
  VariantFinishEnum,
  VariantProductEnum,
} from "@sorcery/db";

/**
 * Variant sorting
 */
export type BaseVariantTypeForSorting = {
  id: string;
  product: VariantProductEnum;
  finish: VariantFinishEnum;
  artist: { slug: string };
  cardId: string;
  setCard: {
    set: { code: SetCodeEnum; name: string };
  };
};
export type BaseCardInDeckTypeForSorting = { quantity: number; variantId: string; id: string } & {
  card: Card & { variants: BaseVariantTypeForSorting[] };
};
export type BaseDeckTypeForSorting = { id: string };

export const finishSortImportance = {
  [VariantFinishEnum.Standard]: 1,
  [VariantFinishEnum.Foil]: 2,
};

export const productSortImportance = {
  [VariantProductEnum.Booster]: 1,
  [VariantProductEnum.Preconstructed_Deck]: 2,
  [VariantProductEnum.Box_Topper]: 3,
  [VariantProductEnum.Draft_Kit]: 4,
  [VariantProductEnum.Store_Kit]: 5,
  [VariantProductEnum.Promo]: 6,
  [VariantProductEnum.Pledge_Pack]: 7,
};

const finishSortComparitor = (v1: BaseVariantTypeForSorting, v2: BaseVariantTypeForSorting) => {
  return (
    finishSortImportance[v1.finish as VariantFinishEnum] -
    finishSortImportance[v2.finish as VariantFinishEnum]
  );
};

const productSortComparitor = (v1: BaseVariantTypeForSorting, v2: BaseVariantTypeForSorting) => {
  return (
    productSortImportance[v1.product as VariantProductEnum] -
    productSortImportance[v2.product as VariantProductEnum]
  );
};

const setImportance = {
  [SetCodeEnum.art]: 1,
  [SetCodeEnum.bet]: 2,
  [SetCodeEnum.alp]: 3,
};

// custom order above to ensure latest set printing shows first
const setSortComparitor = (v1: BaseVariantTypeForSorting, v2: BaseVariantTypeForSorting) =>
  setImportance[v1.setCard.set.code] - setImportance[v2.setCard.set.code];

export const sortCardVariants = (
  cards: { variants: BaseVariantTypeForSorting[] }[],
  set: SetCodeEnum | "*" = "*",
  prioFoils?: boolean,
  artistFilters?: {
    type: FilterTypeEnum.ARTIST;
    value: string;
  }[],
) => {
  const targetSetImportance = {
    [set]: 1, // default everything else to a lower priority
  };
  // note 2 is being used below as the lower priority number than the target set
  // to ensure all other sets are layers after the target
  const targetSetSortComparitor = (v1: BaseVariantTypeForSorting, v2: BaseVariantTypeForSorting) =>
    (targetSetImportance[v1.setCard.set.code] ?? 2) -
    (targetSetImportance[v2.setCard.set.code] ?? 2);

  const targetFinishImportance = {
    [VariantFinishEnum.Foil]: 1, // default everything else to a lower priority
    [VariantFinishEnum.Standard]: 2, // default everything else to a lower priority
  };

  const finishSortComparitor = prioFoils
    ? (v1: BaseVariantTypeForSorting, v2: BaseVariantTypeForSorting) =>
        targetFinishImportance[v1.finish as VariantFinishEnum] -
        targetFinishImportance[v2.finish as VariantFinishEnum]
    : (v1: BaseVariantTypeForSorting, v2: BaseVariantTypeForSorting) =>
        finishSortImportance[v1.finish as VariantFinishEnum] -
        finishSortImportance[v2.finish as VariantFinishEnum];

  let comparitorChain: IThenBy<BaseVariantTypeForSorting>;
  if (set === "*") {
    if (artistFilters?.length) {
      artistFilters.forEach((af, i) => {
        const artistImportance = {
          [af.value]: i + 1, // first filter = highest prio
        };
        const artistSortComparitor = (
          v1: BaseVariantTypeForSorting,
          v2: BaseVariantTypeForSorting,
        ) =>
          // default non matching artist hits to the lowest priority
          (artistImportance[v1.artist.slug] ?? artistFilters.length + 1) -
          (artistImportance[v2.artist.slug] ?? artistFilters.length + 1);
        comparitorChain = firstBy(artistSortComparitor)
          .thenBy(setSortComparitor)
          .thenBy(finishSortComparitor)
          .thenBy(productSortComparitor);
      });
    } else {
      comparitorChain = firstBy(setSortComparitor)
        .thenBy(finishSortComparitor)
        .thenBy(productSortComparitor);
    }
  } else {
    comparitorChain = firstBy(targetSetSortComparitor)
      .thenBy(setSortComparitor)
      .thenBy(finishSortComparitor)
      .thenBy(productSortComparitor);
  }

  cards.forEach((c) => c.variants.sort(comparitorChain));
};

export const sortVariants = (variants: BaseVariantTypeForSorting[]) => {
  // these variants can be from any cards, we want to pull out all the distinct cards then create an importance map with them and a comparitor function
  const cardIds = variants.map((v) => v.cardId);
  const distinctCardIds = [...new Set(cardIds)];
  const groupedCardImportance = distinctCardIds.reduce(
    (acc, curr) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...acc,
      [curr]: distinctCardIds.indexOf(curr) + 1, // lower indexes get better priority scores
    }),
    {} as Record<string, number>,
  );
  const groupedCardSortComparitor = (
    v1: BaseVariantTypeForSorting,
    v2: BaseVariantTypeForSorting,
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
  ) => groupedCardImportance[v1.cardId]! - groupedCardImportance[v2.cardId]!;

  const comparitorChain = firstBy(groupedCardSortComparitor)
    .thenBy(setSortComparitor)
    .thenBy(finishSortComparitor)
    .thenBy(productSortComparitor);

  variants.sort(comparitorChain);
};

export const sortDeckVariants = (dl: BaseCardInDeckTypeForSorting[]) => {
  dl.forEach((d) => {
    const chosenVariantId = d.variantId;
    const chosenVariantIdImportance = {
      // default everything else to a lower priority
      [chosenVariantId]: 1,
    };
    const chosenVariantSortComparitor = (
      v1: BaseVariantTypeForSorting,
      v2: BaseVariantTypeForSorting,
    ) =>
      // note: the value 2 is being used as its lower prio than the "1" that gets applied to the chosen variantId
      (chosenVariantIdImportance[v1.id] ?? 2) - (chosenVariantIdImportance[v2.id] ?? 2);

    const comparitorChain = firstBy(chosenVariantSortComparitor)
      .thenBy(setSortComparitor)
      .thenBy(finishSortComparitor)
      .thenBy(productSortComparitor);

    d.card.variants.sort(comparitorChain);
  });
};

/**
 * Collection sorting
 */

const conditionImportance = {
  [CollectionItemConditionEnum.M]: 1,
  [CollectionItemConditionEnum.NM]: 2,
  [CollectionItemConditionEnum.MP]: 3,
  [CollectionItemConditionEnum.SP]: 4,
  [CollectionItemConditionEnum.HP]: 5,
};

const conditionSortComparitor = (
  i1: { condition: CollectionItemConditionEnum },
  i2: { condition: CollectionItemConditionEnum },
) => {
  return conditionImportance[i1.condition] - conditionImportance[i2.condition];
};

export const sortCollectionItems = (
  items: {
    condition: CollectionItemConditionEnum;
    card: {
      variants: BaseVariantTypeForSorting[];
    };
  }[],
) => {
  const comparitorChain = firstBy(conditionSortComparitor);
  items.sort(comparitorChain);
  items.forEach((i) => {
    sortCardVariants([i.card]);
  });
};

/**
 * Distributor sorting
 */
const regionImportance = {
  [DistributorRegionEnum.USA]: 1,
  [DistributorRegionEnum.Canada]: 2,
  [DistributorRegionEnum.UK]: 3,
  [DistributorRegionEnum.Europe_And_UK]: 4,
  [DistributorRegionEnum.Europe]: 5,
  [DistributorRegionEnum.Scandinavia_And_Baltics]: 6,
  [DistributorRegionEnum.Asia]: 7,
  [DistributorRegionEnum.Australia]: 8,
  [DistributorRegionEnum.New_Zealand]: 9,
};

// note 10 is being used as the lower priority number so regions outside of those listed are shifted below
const regionSortComparitor = (a: Distributor, b: Distributor) => {
  const aRegionKey = Object.keys(regionImportance).find((key) =>
    a.region.toLowerCase().includes(key.toLowerCase()),
  );
  const bRegionKey = Object.keys(regionImportance).find((key) =>
    b.region.toLowerCase().includes(key.toLowerCase()),
  );

  return (
    (regionImportance[aRegionKey as keyof typeof regionImportance] ?? 10) -
    (regionImportance[bRegionKey as keyof typeof regionImportance] ?? 10)
  );
};

const sortRegionAlphabetically = (a: Distributor, b: Distributor) => {
  if (a.region.toLowerCase() < b.region.toLowerCase()) {
    return -1;
  }
  if (a.region.toLowerCase() > b.region.toLowerCase()) {
    return 1;
  }
  return 0;
};

const sortNameAlphabetically = (a: Distributor, b: Distributor) => {
  if (a.name.toLowerCase() < b.name.toLowerCase()) {
    return -1;
  }
  if (a.name.toLowerCase() > b.name.toLowerCase()) {
    return 1;
  }
  return 0;
};

export const sortDistributors = (distributors: Distributor[]) => {
  return distributors.sort(
    firstBy(regionSortComparitor).thenBy(sortRegionAlphabetically).thenBy(sortNameAlphabetically),
  );
};

export const sortStarredDecks = (decks: BaseDeckTypeForSorting[], stars: string[]) => {
  const starSortComparitor = (v1: BaseDeckTypeForSorting, v2: BaseDeckTypeForSorting) =>
    // note: the value 2 is being used as its lower prio than the "1" that gets applied to starred decks
    (stars.some((s) => s === v1.id) ? 1 : 2) - (stars.some((s) => s === v2.id) ? 1 : 2);

  return decks.sort(firstBy(starSortComparitor));
};
